import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';
import './Users.css';

const Users = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRfidTags, setUserRfidTags] = useState([]);
  const [showRfidForm, setShowRfidForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [rfidSearchQuery, setRfidSearchQuery] = useState('');
  const [rfidSearchResults, setRfidSearchResults] = useState([]);
  const [showRfidSearch, setShowRfidSearch] = useState(false);

  // Form state
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });

  const [rfidForm, setRfidForm] = useState({
    rfid_tag: '',
    description: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  const api = apiClient;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    if (api) {
      fetchUsers();
    }
  }, [isAdmin, api, fetchUsers]);

  const fetchUserRfidTags = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/rfid-tags`);
      setUserRfidTags(response.data);
    } catch (err) {
      setUserRfidTags([]);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', createForm);
      setCreateForm({ username: '', email: '', password: '', role: 'user' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (err) {
      setError('Failed to create user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddRfidTag = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await api.post(`/users/${selectedUser.id}/rfid-tags`, rfidForm);
      setRfidForm({ rfid_tag: '', description: '' });
      setShowRfidForm(false);
      fetchUserRfidTags(selectedUser.id);
    } catch (err) {
      setError('Failed to add RFID tag: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      await api.put(`/users/${selectedUser.id}/password`, {
        new_password: passwordForm.newPassword
      });
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      setError(null);
      addToast('Password changed successfully', 'success');
    } catch (err) {
      setError('Failed to change password: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserRfidTags(user.id);
  };

  const handleDeleteRfidTag = async (tagId) => {
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to delete this RFID tag?')) return;

    try {
      await api.delete(`/users/${selectedUser.id}/rfid-tags/${tagId}`);
      addToast('RFID tag deleted', 'success');
      fetchUserRfidTags(selectedUser.id);
    } catch (err) {
      addToast('Failed to delete RFID tag: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleToggleRfidTag = async (tagId, currentActive) => {
    if (!selectedUser) return;

    try {
      await api.patch(`/users/${selectedUser.id}/rfid-tags/${tagId}`, {
        is_active: !currentActive
      });
      addToast(`RFID tag ${currentActive ? 'deactivated' : 'activated'}`, 'success');
      fetchUserRfidTags(selectedUser.id);
    } catch (err) {
      addToast('Failed to toggle RFID tag: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleSearchRfidTags = async () => {
    if (!rfidSearchQuery.trim()) return;
    try {
      const response = await api.get(`/rfid-tags/search?q=${encodeURIComponent(rfidSearchQuery)}`);
      setRfidSearchResults(response.data);
      setShowRfidSearch(true);
    } catch (err) {
      addToast('Failed to search RFID tags: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === user?.id) {
      addToast('Cannot delete your own account', 'warning');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await api.delete(`/users/${userId}`);
      addToast('User deleted successfully', 'success');
      setSelectedUser(null);
      setUserRfidTags([]);
      fetchUsers();
    } catch (err) {
      addToast('Failed to delete user: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (!isAdmin) {
    return (
      <div className="users-container">
        <div className="error-message">
          <h2>Access Denied</h2>
          <p>You need admin privileges to access user management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="users-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>User Management</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="Search RFID tags..."
              value={rfidSearchQuery}
              onChange={(e) => setRfidSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchRfidTags()}
              style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
            />
            <button className="btn btn-secondary" onClick={handleSearchRfidTags}>Search</button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            Create New User
          </button>
        </div>
      </div>

      {/* RFID Search Results */}
      {showRfidSearch && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>RFID Search Results ({rfidSearchResults.length})</h3>
            <button className="close-btn" onClick={() => { setShowRfidSearch(false); setRfidSearchResults([]); setRfidSearchQuery(''); }}>x</button>
          </div>
          {rfidSearchResults.length > 0 ? (
            <div style={{ fontSize: '0.9rem' }}>
              {rfidSearchResults.map(tag => (
                <div key={tag.id} style={{ padding: '0.5rem', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{tag.rfid_tag}</strong>
                    {tag.description && <span style={{ color: '#666' }}> - {tag.description}</span>}
                    <span style={{ color: '#999', marginLeft: '0.5rem' }}>({tag.username})</span>
                  </div>
                  <span className={`tag-status ${tag.is_active ? 'active' : 'inactive'}`}>{tag.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666' }}>No RFID tags found matching "{rfidSearchQuery}"</p>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="users-layout">
        {/* Users List */}
        <div className="users-list">
          <h2>Users ({users.length})</h2>
          <div className="users-table">
            <div className="table-header">
              <div>Username</div>
              <div>Email</div>
              <div>Role</div>
              <div>Status</div>
              <div>Created</div>
            </div>
            {Array.isArray(users) ? users.map(user => (
              <div 
                key={user.id} 
                className={`table-row ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => handleSelectUser(user)}
              >
                <div className="username">{user.username}</div>
                <div className="email">{user.email}</div>
                <div className={`role role-${user.role}`}>{user.role}</div>
                <div className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </div>
                <div className="date">{formatDate(user.created_at)}</div>
              </div>
            )) : (
              <div className="table-row">
                <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '20px'}}>
                  {loading ? 'Loading users...' : error ? error : 'No users found'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Details */}
        {selectedUser && (
          <div className="user-details">
            <h2>User Details</h2>
            <div className="user-info">
              <h3>{selectedUser.username}</h3>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Role:</strong> <span className={`role role-${selectedUser.role}`}>{selectedUser.role}</span></p>
              <p><strong>Status:</strong> <span className={selectedUser.is_active ? 'status active' : 'status inactive'}>{selectedUser.is_active ? 'Active' : 'Inactive'}</span></p>
              <p><strong>Created:</strong> {formatDate(selectedUser.created_at)}</p>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordForm(true)}
                >
                  Change Password
                </button>
                {selectedUser.id !== user?.id && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteUser(selectedUser.id)}
                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                  >
                    Delete User
                  </button>
                )}
              </div>
            </div>

            <div className="rfid-section">
              <div className="section-header">
                <h3>RFID Tags ({userRfidTags.length})</h3>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowRfidForm(true)}
                >
                  Add RFID Tag
                </button>
              </div>
              
              {userRfidTags.length > 0 ? (
                <div className="rfid-tags">
                  {userRfidTags.map(tag => (
                    <div key={tag.id} className="rfid-tag" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="tag-info" style={{ flex: 1 }}>
                        <strong>{tag.rfid_tag}</strong>
                        {tag.description && <span className="tag-desc">- {tag.description}</span>}
                      </div>
                      <button
                        onClick={() => handleToggleRfidTag(tag.id, tag.is_active)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: tag.is_active ? '#28a745' : '#6c757d',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          minWidth: '70px'
                        }}
                      >
                        {tag.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => handleDeleteRfidTag(tag.id)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        title="Delete RFID tag"
                      >
                        X
                      </button>
                      <div className="tag-date" style={{ fontSize: '0.75rem', color: '#999' }}>{formatDate(tag.created_at)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-tags">No RFID tags assigned</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New User</h2>
              <button 
                className="close-btn"
                onClick={() => setShowCreateForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-buttons">
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add RFID Tag Modal */}
      {showRfidForm && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Add RFID Tag for {selectedUser.username}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowRfidForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddRfidTag}>
              <div className="form-group">
                <label>RFID Tag:</label>
                <input
                  type="text"
                  value={rfidForm.rfid_tag}
                  onChange={(e) => setRfidForm({...rfidForm, rfid_tag: e.target.value})}
                  placeholder="Enter RFID tag ID"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description (Optional):</label>
                <input
                  type="text"
                  value={rfidForm.description}
                  onChange={(e) => setRfidForm({...rfidForm, description: e.target.value})}
                  placeholder="e.g., Main card, Backup card"
                />
              </div>
              <div className="form-buttons">
                <button type="button" onClick={() => setShowRfidForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add RFID Tag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordForm && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Change Password for {selectedUser.username}</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ newPassword: '', confirmPassword: '' });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>New Password:</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  placeholder="Enter new password (min. 8 characters)"
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label>Confirm Password:</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <div className="form-buttons">
                <button type="button" onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ newPassword: '', confirmPassword: '' });
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;